import dayjs from 'dayjs';
import 'dayjs/locale/it';
import ReactECharts from 'echarts-for-react';
import { chartGrid, chartText } from '../../lib/chartColors';

dayjs.locale('it');

const componentColors = {
  Netto: '#285075',
  Accisa: '#755a00',
  IVA: '#b51f49',
  'Prezzo finale': '#24191c',
};

const euro = value => new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
}).format(Number(value));

export const getFiscalMetrics = item => {
  const fiscal = Number(item?.excise || 0) + Number(item?.vat || 0);
  const share = Number(item?.gross) > 0 ? fiscal / Number(item.gross) * 100 : 0;
  return { fiscal, share };
};

export default function PriceCompositionChart({ items = [], height = 420 }) {
  const months = items.map(item => item.month);
  const series = [
    ['Netto', 'net'],
    ['Accisa', 'excise'],
    ['IVA', 'vat'],
  ].map(([name, key]) => ({
    name,
    type: 'bar',
    stack: 'price',
    data: items.map(item => Number(item[key])),
    barMaxWidth: 34,
    itemStyle: { color: componentColors[name] },
    emphasis: { focus: 'series' },
  }));

  series.push({
    name: 'Prezzo finale',
    type: 'line',
    data: items.map(item => Number(item.gross)),
    symbol: 'circle',
    symbolSize: 6,
    lineStyle: { color: componentColors['Prezzo finale'], width: 2.5 },
    itemStyle: { color: componentColors['Prezzo finale'] },
    z: 5,
  });

  return <ReactECharts style={{ height, width: '100%', minWidth: 0 }} option={{
    animationDuration: 420,
    animationEasing: 'cubicOut',
    grid: { left: 54, right: 14, top: 72, bottom: 48 },
    legend: {
      top: 4,
      left: 4,
      type: 'scroll',
      textStyle: { fontFamily: 'Rubik', fontSize: 11, color: chartText, fontWeight: 500 },
      itemWidth: 18,
      itemHeight: 8,
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      triggerOn: 'mousemove|click',
      backgroundColor: '#fff8f9',
      borderWidth: 0,
      padding: 14,
      textStyle: { fontFamily: 'Rubik', color: '#24191c' },
      extraCssText: 'border-radius:18px;box-shadow:0 2px 8px rgba(35,25,27,.14);',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(40,80,117,.08)' } },
      formatter: params => {
        const index = params?.[0]?.dataIndex ?? 0;
        const item = items[index];
        if (!item) return '';
        const { fiscal, share } = getFiscalMetrics(item);
        const title = dayjs(`${item.month}-01`).format('MMMM YYYY');
        const row = (label, value, color) => `<div style="display:flex;justify-content:space-between;gap:24px;margin-top:5px"><span><i style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${color};margin-right:7px"></i>${label}</span><b>${euro(value)} €/l</b></div>`;
        return `<div style="min-width:220px"><b style="text-transform:capitalize">${title}</b>${row('Prezzo finale', item.gross, componentColors['Prezzo finale'])}${row('Netto', item.net, componentColors.Netto)}${row('Accisa', item.excise, componentColors.Accisa)}${row('IVA', item.vat, componentColors.IVA)}<div style="height:1px;background:#dbc1c8;margin:9px 0 6px"></div><div style="display:flex;justify-content:space-between;gap:24px"><span>Fiscalità totale</span><b>${euro(fiscal)} €/l</b></div><div style="display:flex;justify-content:space-between;gap:24px;margin-top:4px"><span>Quota fiscale</span><b>${share.toFixed(1).replace('.', ',')}%</b></div></div>`;
      },
    },
    xAxis: {
      type: 'category',
      data: months,
      axisLabel: { hideOverlap: true, color: chartText, fontFamily: 'Rubik', fontSize: 10, formatter: value => dayjs(`${value}-01`).format('MMM YY') },
      axisLine: { lineStyle: { color: chartGrid } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      axisLabel: { color: chartText, fontFamily: 'Rubik', fontSize: 10, formatter: value => `${Number(value).toFixed(2)} €` },
      splitLine: { lineStyle: { color: chartGrid } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    dataZoom: [{ type: 'inside', filterMode: 'none' }],
    series,
  }} />;
}
